import { normalizeKey } from "../utils/normalizeDietKey";
import {
  COMPACT_NUTRIENT_KEYS,
  compactFromScaledNutrition,
  emptyCompactTotals,
} from "./calculateMealNutrients";
import { formatNutrientValue } from "./formatNutrientValue";

export { COMPACT_NUTRIENT_KEYS, emptyCompactTotals };

/** Macro keys only — everything except calories. */
const MACRO_KEYS = COMPACT_NUTRIENT_KEYS.filter((k) => k !== "calories");

/**
 * Normalize API totals (compact, singular, or legacy USDA keys) for display
 * cards. Singular `protein`/`sugar` are accepted because target-nutrient goals
 * use that spelling; without them those two macros silently render as 0 while
 * calories (single spelling) look correct.
 */
const roundVal = formatNutrientValue;

export function toCompactNutrientTotals(totals = {}) {
  return {
    calories: roundVal(totals.calories ?? totals.total_energy_kcal ?? 0),
    proteins: roundVal(totals.proteins ?? totals.protein ?? totals.total_protein ?? 0),
    carbs: roundVal(totals.carbs ?? totals["total_carbohydrate,_by_difference"] ?? 0),
    fats: roundVal(totals.fats ?? totals["total_total_lipid_(fat)"] ?? 0),
    fiber: roundVal(totals.fiber ?? totals["total_fiber,_total_dietary"] ?? 0),
    sugars: roundVal(totals.sugars ?? totals.sugar ?? totals["total_sugars,_total"] ?? 0),
  };
}

export const scaledNutritionToDisplay = toCompactNutrientTotals;

export function sumTotalsByOption(totalsByOption = {}) {
  const sum = emptyCompactTotals();
  Object.values(totalsByOption).forEach((opt) => {
    COMPACT_NUTRIENT_KEYS.forEach((key) => {
      sum[key] += Number(opt[key] ?? 0);
    });
  });
  COMPACT_NUTRIENT_KEYS.forEach((key) => {
    sum[key] = parseFloat(sum[key].toFixed(2));
  });
  return sum;
}

export function averageCompactTotalsAcrossOptions(byOption = {}) {
  const opts = Object.values(byOption);
  const avg = emptyCompactTotals();
  if (!opts.length) return avg;
  opts.forEach((t) => {
    COMPACT_NUTRIENT_KEYS.forEach((key) => {
      avg[key] += Number(t[key] ?? 0);
    });
  });
  const n = opts.length;
  COMPACT_NUTRIENT_KEYS.forEach((key) => {
    avg[key] = Math.round((avg[key] / n) * 100) / 100;
  });
  return avg;
}

/** Sum calories across option totals in a nutrition summary. */
export function sumSummaryCalories(summary) {
  if (!summary || typeof summary !== "object") return 0;
  const byOpt = summary.totalsByOption || summary.byOption || {};
  return Object.values(byOpt).reduce(
    (sum, t) => sum + (Number(t?.calories) || 0),
    0
  );
}

/**
 * True when any option total carries a non-calorie macro. Used to stop a
 * calories-only API summary from beating a macro-complete live one purely on
 * calorie count, which would render every macro card as 0g.
 */
export function summaryHasMacros(summary) {
  if (!summary || typeof summary !== "object") return false;
  const byOpt = summary.totalsByOption || summary.byOption || {};
  return Object.values(byOpt).some((totals) => {
    const compact = toCompactNutrientTotals(totals || {});
    return MACRO_KEYS.some((key) => Number(compact[key]) > 0);
  });
}

function addIntoTotals(target, delta, sign = 1) {
  if (!target || !delta) return target;
  const next = { ...target };
  COMPACT_NUTRIENT_KEYS.forEach((key) => {
    const v = Number(next[key] ?? 0) + sign * (Number(delta[key]) || 0);
    next[key] = parseFloat(Math.max(0, v).toFixed(2));
  });
  return next;
}

/**
 * Optimistically bump (or subtract) a plan/template nutrition_summary when a
 * food is added/removed so toolbar totals move immediately without relying on
 * incomplete live scaling of the full meal tree.
 */
export function bumpNutritionSummary(
  summary,
  { periodName, optionTab, scaled, sign = 1 } = {}
) {
  const delta = toCompactNutrientTotals(scaled || {});
  if (!summary || typeof summary !== "object") return summary ?? null;
  if (!COMPACT_NUTRIENT_KEYS.some((k) => Number(delta[k]) > 0)) return summary;

  const opt = String(optionTab || "").match(/\d+/)?.[0] || "1";
  const meal = periodName || "";
  const rowKey = meal ? `${meal}|Option ${opt}` : null;

  const totalsByOption = {
    ...(summary.totalsByOption || summary.byOption || {}),
  };
  totalsByOption[opt] = addIntoTotals(
    totalsByOption[opt] || emptyCompactTotals(),
    delta,
    sign
  );

  const byMealType = { ...(summary.byMealType || {}) };
  if (meal) {
    const mealOpts = { ...(byMealType[meal] || {}) };
    mealOpts[opt] = addIntoTotals(
      mealOpts[opt] || emptyCompactTotals(),
      delta,
      sign
    );
    byMealType[meal] = mealOpts;
  }

  const rowCaloriesMap = { ...(summary.rowCaloriesMap || {}) };
  const rowTotalsMap = { ...(summary.rowTotalsMap || {}) };
  if (rowKey) {
    rowCaloriesMap[rowKey] = parseFloat(
      Math.max(
        0,
        (Number(rowCaloriesMap[rowKey]) || 0) + sign * (Number(delta.calories) || 0)
      ).toFixed(2)
    );
    rowTotalsMap[rowKey] = addIntoTotals(
      rowTotalsMap[rowKey] || emptyCompactTotals(),
      delta,
      sign
    );
  }

  return {
    ...summary,
    totalsByOption,
    byOption: totalsByOption,
    byMealType,
    rowCaloriesMap,
    rowTotalsMap,
    averageAcrossOptions: averageCompactTotalsAcrossOptions(totalsByOption),
  };
}

/**
 * Resolve a `"MealType|Option N"` row entry from one of the summary row maps.
 * Tries the exact key first, then scans with normalized meal-type matching so
 * casing/format variants still hit.
 */
function lookupRowEntry(map, mealType, optionNum) {
  if (!map) return null;

  const opt = String(optionNum);
  const exact = `${mealType}|Option ${opt}`;
  if (map[exact] != null) return map[exact];

  const targetMeal = normalizeKey(mealType);
  for (const [key, val] of Object.entries(map)) {
    const pipe = key.indexOf("|");
    if (pipe < 0) continue;
    if (normalizeKey(key.slice(0, pipe)) !== targetMeal) continue;
    if (key.slice(pipe + 1).match(/\d+/)?.[0] !== opt) continue;
    return val;
  }
  return null;
}

/** Full macro totals for a single meal-type × option row, or null. */
export function lookupRowTotals(summary, mealType, optionNum) {
  if (!summary || mealType == null || optionNum == null) return null;

  const entry = lookupRowEntry(summary.rowTotalsMap, mealType, optionNum);
  if (entry && typeof entry === "object") return toCompactNutrientTotals(entry);
  return null;
}

export function lookupRowCalories(summary, mealType, optionNum) {
  if (!summary || mealType == null || optionNum == null) return null;

  const fromCalories = lookupRowEntry(
    summary.rowCaloriesMap,
    mealType,
    optionNum
  );
  if (typeof fromCalories === "number") return Number(fromCalories);
  if (fromCalories?.calories != null) return Number(fromCalories.calories);

  const totals = lookupRowTotals(summary, mealType, optionNum);
  return totals ? totals.calories : null;
}

/** Sum all six macros across the foods a row currently shows; null if empty. */
export function sumScaledTotalsFromFoods(foods = []) {
  const sum = emptyCompactTotals();
  foods.forEach((food) => {
    const compact = compactFromScaledNutrition(food?.scaled_nutrition);
    if (!compact) return;
    COMPACT_NUTRIENT_KEYS.forEach((key) => {
      sum[key] += Number(compact[key]) || 0;
    });
  });

  let hasValue = false;
  COMPACT_NUTRIENT_KEYS.forEach((key) => {
    sum[key] = Math.round(sum[key] * 100) / 100;
    if (sum[key] > 0) hasValue = true;
  });
  return hasValue ? sum : null;
}

export function buildMealFoodDetailsMap(meals = [], existingMap = {}) {
  const detailsMap = { ...existingMap };
  meals.forEach((meal) => {
    meal.meal_options?.forEach((option) => {
      option.foods?.forEach((food) => {
        const id = food?.food_id || food?.target_food_id;
        if (!id) return;
        if (food.recipe_details) {
          detailsMap[String(id)] = food.recipe_details;
        }
      });
    });
  });
  return detailsMap;
}

export function scaledNutritionMapFromTargetPlans(targetPlans = []) {
  const map = {};
  targetPlans.forEach((plan) => {
    (plan.meals || []).forEach((meal) => {
      (meal.meal_options || []).forEach((opt) => {
        (opt.foods || []).forEach((food) => {
          if (food?.target_food_id && food.scaled_nutrition) {
            map[String(food.target_food_id)] = food.scaled_nutrition;
          }
        });
      });
    });
  });
  return map;
}

export function scaledNutritionMapFromActualTrackings(actualTrackings = []) {
  const map = {};
  actualTrackings.forEach((tracking) => {
    (tracking.meals || []).forEach((meal) => {
      const rows = meal.meal_foods || meal.foods || [];
      rows.forEach((food) => {
        if (food?.actual_food_id && food.scaled_nutrition) {
          map[String(food.actual_food_id)] = food.scaled_nutrition;
        }
      });
    });
  });
  return map;
}
